import React, { ReactNode } from 'react';

import { Box, Collapse, Divider, Paper, Typography, useTheme } from '@material-ui/core';
import clsx from 'clsx';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';

import { SettingsStatusPill, SettingsStatusTone } from './SettingsStatusPill';
import { useStyles } from './styles';

interface Props {
  title: string;
  description: string;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  status?: string;
  statusTone?: SettingsStatusTone;
  /** Switch for features that can be turned on and off. Flipping it never opens the card. */
  headerControl?: ReactNode;
  /** Pinned bottom-right of the card, below the body. */
  actions?: ReactNode;
  /** Sits bottom-left next to the actions — secondary links such as "Restore defaults". */
  secondaryAction?: ReactNode;
  /**
   * Keeps the body in the DOM while collapsed. Needed for cards whose fields belong to a
   * page-level form — a collapsed card must not drop its values or its validation.
   */
  keepMounted?: boolean;
  /** Tighter card for the ones that sit inside another card's body, e.g. one alert in a tile. */
  nested?: boolean;
  children?: ReactNode;
}

/**
 * The one card pattern every settings widget uses: name, one plain sentence, the value
 * it is set to right now, and a body that opens from the header.
 */
export const SettingsCard = ({
  title,
  description,
  expanded,
  onExpandedChange,
  status,
  statusTone,
  headerControl,
  actions,
  secondaryAction,
  keepMounted = false,
  nested = false,
  children,
}: Props) => {
  const classes = useStyles();
  const theme = useTheme();
  const chevronColor = theme.palette.text.secondary;

  const toggle = () => onExpandedChange(!expanded);

  return (
    <Paper variant="outlined" className={clsx(classes.card, { [classes.nestedCard]: nested })}>
      <Box
        className={clsx(classes.header, { [classes.nestedHeader]: nested })}
        onClick={toggle}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
      >
        <Box className={classes.headerText}>
          <Typography className={classes.title}>{title}</Typography>

          <Typography variant="caption" className={classes.description}>
            {description}
          </Typography>

          {!!status && <SettingsStatusPill label={status} tone={statusTone} />}
        </Box>

        <Box className={classes.headerControls}>
          {!!headerControl && <Box onClick={e => e.stopPropagation()}>{headerControl}</Box>}

          {expanded ? (
            <FiChevronUp size={20} color={chevronColor} />
          ) : (
            <FiChevronDown size={20} color={chevronColor} />
          )}
        </Box>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit={!keepMounted}>
        <Divider />

        <Box className={clsx(classes.body, { [classes.nestedBody]: nested })}>{children}</Box>

        {(!!actions || !!secondaryAction) && (
          <Box className={clsx(classes.footer, { [classes.nestedFooter]: nested })}>
            {secondaryAction}
            <Box className={classes.footerActions}>{actions}</Box>
          </Box>
        )}
      </Collapse>
    </Paper>
  );
};
